import { Modal } from '@everlywell/leaves';
import InfoCard, { InfoCardProps } from 'components/InfoCard/InfoCard';
import React from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { sanitize } from 'utils/domHelpers';
import { CTA } from 'utils/types';

import * as S from './InfoCardsModal.styles';

export type InfoCardsModalProps = {
  content: {
    title?: string;
    subHeadline?: string;
    cards?: InfoCardProps[];
    disclaimerText?: string;
    description?: string;
    // To avoid changing Contentful due to the re-architecture, this will use the Link content model from Contentful.
    cta?: CTA;
  };
  onCtaClick?: () => void;
  isOpen: boolean;
  openHandler: (x: boolean) => void;
};

/**
 * Full screen modal for info cards with titles
 */
export default function InfoCardsModal({
  isOpen,
  openHandler,
  onCtaClick,
  content: { title, subHeadline, description, disclaimerText, cta, cards },
}: InfoCardsModalProps): JSX.Element | null {
  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick();
    }

    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title,
        contentfulSection: 'InfoCardsModalSection',
        ctaText: cta?.text,
      },
    });
  };

  return isOpen ? (
    <S.Container>
      <Modal open={isOpen} openHandler={openHandler} forceFullScreen>
        {title && <S.Title>{title}</S.Title>}

        <S.CardWrapper>
          {cards?.map((card) => (
            <InfoCard key={card.id} addBorder={true} reducedCard {...card} />
          ))}
        </S.CardWrapper>

        <S.ExtraInfo>
          {subHeadline && <S.SubHeadline>{subHeadline}</S.SubHeadline>}
          {description ? (
            <S.LinkWrapper
              dangerouslySetInnerHTML={{
                __html: sanitize(description),
              }}
            ></S.LinkWrapper>
          ) : null}

          <S.HorizontalLine />

          {disclaimerText && (
            <S.DisclaimerText>{disclaimerText}</S.DisclaimerText>
          )}

          <S.Cta appearance="primary" onClick={handleCtaClick}>
            {cta?.text}
          </S.Cta>
        </S.ExtraInfo>
      </Modal>
    </S.Container>
  ) : null;
}
